import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormCheckbox } from '@ipfabric/ui-components/FormCheckbox/FormCheckbox';
import { PageHeader } from '@ipfabric/ui-components/PageHeader/PageHeader';
import { RadioField } from '@ipfabric/ui-components/RadioField/RadioField';
import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { date } from '@ipfabric/utils';
import { ApiTableView } from 'components/ApiTable/components/ApiTableView';
import { useApiTable } from 'components/ApiTable/model/useApiTable';
import FormErrorMessage from 'components/FormErrorMessage';
import { JOB_STATUSES } from 'containers/App/Header/JobManagement/constants';
import type { ImmutableVerifyJob } from 'containers/App/Header/JobManagement/jobTypes';
import {
  makeSelectAnySnapshotVerifyRunning,
  makeSelectTechsupportGenerating,
} from 'containers/App/Header/JobManagement/selectors';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import type Immutable from 'immutable';
import { fetchSnapshotManagement } from 'pages/SnapshotManagement/actions';
import { makeSelectAllSnapshots } from 'pages/SnapshotManagement/selectors';
import type { ImmutableSnapshot } from 'pages/SnapshotManagement/snapshotManagementTypes';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { workaroundExtractModelCurrentState } from 'utils/hooks/useCommandModel';
import { useDateTimeFormat } from 'utils/hooks/useDateTimeFormat';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import { useTimezone } from 'utils/hooks/useTimeZone';
import { useApi } from 'utils/request/useApi';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';
import {
  type GenerateTechsupportModalData,
  GenerateTechsupportSideModal,
} from './GenerateTechsupportModal';
import {
  DEVICE_TABLE_ENDPOINT_CONFIG,
  INITIAL_VALUES,
  NO_DEVICE_SELECTED_MESSAGE,
  NO_OPTION_SELECTED_MESSAGE,
  type PreviousSelectionValues,
  type TechsupportFormValues,
  createRequestBody,
  createSelectedFeatureLabels,
  createVerifyRequestBody,
  hasSelectedOption,
  syncLimitDeviceDependencies,
  syncSelectionDependencies,
} from './Techsupport.utils';
import { useInitialLimitSns } from './useInitialLimitSns';

type SelectedDeviceRow = {
  hostname?: string | null;
  sn: string;
};

type DeviceTableSelectionState = {
  rowCount?: number | null;
  selectedRows: Set<string>;
};

type VerifySnapshotRedirectState = {
  redirected: boolean;
  snapshotId: string;
};

const NO_SNAPSHOT_SELECTED_MESSAGE = 'No active snapshot selected';

const hasActiveSnapshotId = (id: string | null): id is string =>
  id !== null && id.length > 0;

const getNoOptionErrorMessage = (rootErrorMessage: string | undefined) =>
  rootErrorMessage === NO_OPTION_SELECTED_MESSAGE
    ? rootErrorMessage
    : undefined;

const getSnapshotSettingsErrorMessage = (
  rootErrorMessage: string | undefined,
) =>
  rootErrorMessage === NO_OPTION_SELECTED_MESSAGE
    ? undefined
    : rootErrorMessage;

const renderErrorMessage = (message: string | undefined, className?: string) =>
  message ? (
    <FormErrorMessage className={className}>{message}</FormErrorMessage>
  ) : null;

const TECHSUPPORT_PATHNAME = '/support/techsupport';

const TechsupportPage = () => {
  const api = useApi();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { showNotification } = useNotifications();
  const snapshotId = useSnapshotId();
  const dateTimeFormat = useDateTimeFormat();
  const timezone = useTimezone();
  const deviceTableEndpoint = useTableEndpoint(DEVICE_TABLE_ENDPOINT_CONFIG);
  const initialLimitSns = useInitialLimitSns();
  const defaultValues = useMemo(
    () =>
      initialLimitSns.length > 0
        ? {
            ...INITIAL_VALUES,
            discoveryServicesLogs: false,
            limitDevices: true,
            ipfChecker: false,
            systemLogs: false,
          }
        : INITIAL_VALUES,
    [initialLimitSns.length],
  );
  const deviceTableModel = useApiTable({
    bulkActions: [],
    endpoint: deviceTableEndpoint,
    initialSelectedRows: initialLimitSns,
    rowIdField: 'sn',
    titleEndAdornment: (
      <TooltipIcon
        className='ml-1'
        content='Select the devices whose snapshot data will be included in the techsupport file.'
      />
    ),
  });
  const techsupport = useSelector(makeSelectTechsupportGenerating());
  const verifyRunningJob = useSelector<unknown, ImmutableVerifyJob | undefined>(
    useMemo(() => makeSelectAnySnapshotVerifyRunning(), []),
  );
  const snapshots = useSelector<unknown, Immutable.List<ImmutableSnapshot>>(
    useMemo(() => makeSelectAllSnapshots(), []),
  );
  const [showGenerateTechsupportModal, setShowGenerateTechsupportModal] =
    useState(false);
  const [generateTechsupportModalData, setGenerateTechsupportModalData] =
    useState<GenerateTechsupportModalData | null>(null);
  const [verifySnapshotRedirectState, setVerifySnapshotRedirectState] =
    useState<VerifySnapshotRedirectState | null>(null);
  const [hasSubmittedVerify, setHasSubmittedVerify] = useState(false);
  const {
    clearErrors,
    control,
    formState,
    getValues,
    handleSubmit,
    setError,
    setValue,
    watch,
  } = useCustomForm<TechsupportFormValues>({
    defaultValues,
  });

  const formValues: TechsupportFormValues = {
    databases: watch('databases'),
    discoveryServicesLogs: watch('discoveryServicesLogs'),
    ipfChecker: watch('ipfChecker'),
    limitDevices: watch('limitDevices'),
    snapshot: watch('snapshot'),
    snapshotBackupDb: watch('snapshotBackupDb'),
    snapshotIncludeCli: watch('snapshotIncludeCli'),
    systemLogs: watch('systemLogs'),
    verifySnapshot: watch('verifySnapshot'),
  };
  const {
    databases,
    discoveryServicesLogs,
    limitDevices,
    snapshot,
    snapshotBackupDb,
    snapshotIncludeCli,
  } = formValues;
  const isNoSnapshotData = !snapshot && !databases;
  const requiresActiveSnapshot = snapshot;
  const previousValues = useRef<PreviousSelectionValues>({
    databases,
    snapshot,
  });
  const running = Boolean(techsupport);
  const hasAnySelectedOption = hasSelectedOption(formValues);
  const showDeviceSelection = !running && snapshot && limitDevices;
  const rootErrorMessage = formState.errors.root?.message;
  const noOptionErrorMessage = getNoOptionErrorMessage(rootErrorMessage);
  const snapshotSettingsErrorMessage =
    getSnapshotSettingsErrorMessage(rootErrorMessage);

  useEffect(() => {
    syncSelectionDependencies({
      databases,
      previousValues: previousValues.current,
      setValue,
      snapshot,
      snapshotBackupDb,
      snapshotIncludeCli,
    });

    previousValues.current = {
      databases,
      snapshot,
    };
  }, [databases, setValue, snapshot, snapshotBackupDb, snapshotIncludeCli]);

  useEffect(() => {
    syncLimitDeviceDependencies({
      databases,
      discoveryServicesLogs,
      limitDevices,
      setValue,
      snapshot,
      snapshotBackupDb,
    });
  }, [
    databases,
    discoveryServicesLogs,
    limitDevices,
    setValue,
    snapshot,
    snapshotBackupDb,
  ]);

  useEffect(() => {
    if (
      hasAnySelectedOption &&
      rootErrorMessage === NO_OPTION_SELECTED_MESSAGE
    ) {
      clearErrors('root');
    }
  }, [clearErrors, hasAnySelectedOption, rootErrorMessage]);

  useEffect(() => {
    if (!limitDevices && rootErrorMessage === NO_DEVICE_SELECTED_MESSAGE) {
      clearErrors('root');
    }
  }, [clearErrors, limitDevices, rootErrorMessage]);

  useEffect(() => {
    // Only redirect for a verify the user kicked off from this page mount. The
    // selector reports any running verify (other tabs, jobs already in flight
    // on arrival), and without this gate we'd yank the user away from
    // techsupport without their input.
    if (!hasSubmittedVerify) {
      return;
    }

    if (verifyRunningJob?.get('status') !== JOB_STATUSES.running) {
      return;
    }

    // Multiple techsupport tabs share the verifyRunningJob selector, so scope
    // the redirect to the tab whose snapshot was verified and whose user is
    // actually on the techsupport page — otherwise tab B yanks itself away
    // when tab A starts a verify on a different snapshot.
    const sourceSnapshotId = verifyRunningJob.getIn(['args', 'snapshot', 'id']);
    if (pathname !== TECHSUPPORT_PATHNAME || sourceSnapshotId !== snapshotId) {
      return;
    }

    const verifiedSnapshotId = verifyRunningJob.getIn(['args', 'newKey']);

    if (
      typeof verifiedSnapshotId === 'string' &&
      verifiedSnapshotId.length > 0
    ) {
      if (verifySnapshotRedirectState?.snapshotId === verifiedSnapshotId) {
        return;
      }

      // The verify job emits newKey after backend promotion, but Snapshot
      // Management validates route ids against its Redux snapshot list. Refresh
      // that list first; the effect below waits until this id is present.
      dispatch(fetchSnapshotManagement());
      setVerifySnapshotRedirectState({
        redirected: false,
        snapshotId: verifiedSnapshotId,
      });
    }
  }, [
    hasSubmittedVerify,
    pathname,
    snapshotId,
    verifyRunningJob,
    verifySnapshotRedirectState?.snapshotId,
  ]);

  useEffect(() => {
    // Redirect only after the refreshed list contains the promoted snapshot.
    // Otherwise Snapshot Management may treat the route id as unknown and send
    // the user back to the new-snapshot page.
    if (
      verifySnapshotRedirectState === null ||
      verifySnapshotRedirectState.redirected ||
      !snapshots.some(
        (item) => item.get('id') === verifySnapshotRedirectState.snapshotId,
      )
    ) {
      return;
    }

    navigate(`/snapshot-management/${verifySnapshotRedirectState.snapshotId}`);
    setVerifySnapshotRedirectState({
      ...verifySnapshotRedirectState,
      redirected: true,
    });
  }, [snapshots, verifySnapshotRedirectState]);

  const getDeviceTableSelectionState = (): DeviceTableSelectionState =>
    workaroundExtractModelCurrentState(
      deviceTableModel.tableModel.dispatch,
    ) as DeviceTableSelectionState;

  const getSelectedSerialNumbers = (): string[] =>
    Array.from(getDeviceTableSelectionState().selectedRows.values());

  const isAllUnfilteredSnapshotDevicesSelected = (
    limitSn: string[] | null,
  ): boolean => {
    if (limitSn === null || limitSn.length === 0) {
      return false;
    }

    const deviceTableSelectionState = getDeviceTableSelectionState();
    const noActiveDeviceFilters =
      Object.keys(deviceTableModel.filters ?? {}).length === 0;

    if (!noActiveDeviceFilters) {
      return false;
    }

    return (
      typeof deviceTableSelectionState.rowCount === 'number' &&
      limitSn.length === deviceTableSelectionState.rowCount
    );
  };

  const createSelectedSnapshotDeviceLabels = async (
    limitSn: string[],
  ): Promise<string[]> => {
    try {
      const response = await api.request<{ data: SelectedDeviceRow[] }>({
        data: {
          columns: ['hostname', 'sn'],
          filters: {
            or: limitSn.map((sn) => ({ sn: ['eq', sn] })),
          },
          snapshot: snapshotId,
        },
        method: 'POST',
        url: '/tables/inventory/devices',
      });
      const rowBySn = new Map(response.data.map((row) => [row.sn, row]));

      return limitSn.map((sn) => rowBySn.get(sn)?.hostname || sn);
    } catch {
      // Hostnames are display-only; generating should not depend on this lookup.
      return limitSn;
    }
  };

  const openGenerateConfirmation: SubmitHandler<TechsupportFormValues> = (
    _values,
  ) => {
    const currentValues = getValues();

    if (!hasSelectedOption(currentValues)) {
      setError('root', {
        message: NO_OPTION_SELECTED_MESSAGE,
      });
      return;
    }

    const limitSn = currentValues.limitDevices
      ? getSelectedSerialNumbers()
      : null;

    if (limitSn?.length === 0) {
      setError('root', {
        message: NO_DEVICE_SELECTED_MESSAGE,
      });
      return;
    }

    clearErrors('root');

    setValue('verifySnapshot', false);
    setGenerateTechsupportModalData({
      values: { ...currentValues },
      limitSn,
      allSnapshotDevicesSelected:
        limitSn === null || isAllUnfilteredSnapshotDevicesSelected(limitSn),
      selectedFeatureLabels: createSelectedFeatureLabels(currentValues),
    });
    setShowGenerateTechsupportModal(true);
  };

  const requestVerifySnapshot = async (
    values: TechsupportFormValues,
    limitSn: string[],
  ): Promise<void> => {
    if (!hasActiveSnapshotId(snapshotId)) {
      showNotification({
        type: 'danger',
        message: NO_SNAPSHOT_SELECTED_MESSAGE,
      });
      return;
    }

    try {
      await api.request({
        data: createVerifyRequestBody(values, snapshotId, limitSn),
        method: 'POST',
        url: '/os/verify-snapshot',
      });
      setHasSubmittedVerify(true);
    } catch (error) {
      showNotification({
        type: 'danger',
        title: 'Snapshot verification failed',
        message: error instanceof Error ? error.message : '',
      });
      throw error;
    }
  };

  const submitGenerateTechsupport = handleSubmit(async (values) => {
    if (generateTechsupportModalData === null) {
      return;
    }

    const panelValues = generateTechsupportModalData.values;
    if (panelValues.snapshot && !hasActiveSnapshotId(snapshotId)) {
      showNotification({
        type: 'danger',
        message: NO_SNAPSHOT_SELECTED_MESSAGE,
      });
      return;
    }

    const modalLimitSn = generateTechsupportModalData.limitSn;
    const canVerify =
      values.verifySnapshot && modalLimitSn !== null && modalLimitSn.length > 0;

    const techsupportPromise = api
      .request({
        data: createRequestBody(
          generateTechsupportModalData.values,
          snapshotId,
          generateTechsupportModalData.limitSn,
        ),
        method: 'POST',
        url: '/os/techsupport',
      })
      .catch((error) => {
        showNotification({
          type: 'danger',
          title: 'Techsupport generation failed',
          message: error instanceof Error ? error.message : '',
        });
        throw error;
      });

    const requests = [techsupportPromise];

    if (canVerify && modalLimitSn !== null) {
      requests.push(
        requestVerifySnapshot(
          generateTechsupportModalData.values,
          modalLimitSn,
        ),
      );
    }

    await Promise.allSettled(requests);
    setShowGenerateTechsupportModal(false);
  });

  const submitGenerateForm = handleSubmit(openGenerateConfirmation);
  const submitGenerateSafely = (event?: FormEvent<HTMLFormElement>): void => {
    void submitGenerateForm(event).catch(() => undefined);
  };
  const bottomTextRunning = `The process is running, request created at ${date.formatUtcTimestampInTimeZone(
    techsupport?.get('createdAt') ?? 0,
    timezone,
    dateTimeFormat,
  )}`;

  return (
    <div className='flex flex-col flex-1 items-center'>
      <PageHeader
        elevated
        width='content-default'
        label='Techsupport file generation'
        buttons={
          <Button
            variant='primary'
            disabled={
              running || (requiresActiveSnapshot && snapshotId === null)
            }
            loading={formState.isSubmitting}
            onClick={() => {
              submitGenerateSafely();
            }}
          >
            Generate file
          </Button>
        }
      />
      <div className='w-full max-w-content-default mt-2xl mb flex flex-col flex-1'>
        {running ? (
          <Card className='p'>
            <LoaderAndText
              text='You can continue to work, you will be notified about result in the queue job'
              bottomText={bottomTextRunning}
              errorText="Can't load data"
              delay={5000}
            />
          </Card>
        ) : (
          <form onSubmit={submitGenerateSafely}>
            <fieldset
              disabled={running || formState.isSubmitting}
              className='flex flex-col gap'
            >
              {/* Card 1: General settings */}
              <Card className='p flex flex-col gap'>
                <div className='flex items-center gap-1'>
                  <div className='text-level-5 font-semibold'>
                    General settings
                  </div>
                  <TooltipIcon
                    className='ml-1'
                    content='Include diagnostic data and logs that help IP Fabric Support troubleshoot system and discovery issues.'
                  />
                </div>
                <p className='text-sm text-default'>
                  These options include system diagnostics, platform logs, and
                  discovery service logs that help IP Fabric Support investigate
                  technical issues.
                </p>
                <div className='flex flex-col gap'>
                  <FormCheckbox
                    control={control}
                    name='ipfChecker'
                    description='System environmental data'
                    helperText='Results of internal environment and system health checks used for troubleshooting.'
                  />
                  <FormCheckbox
                    control={control}
                    name='systemLogs'
                    description='System logs'
                    helperText='System, database, messaging, and API service logs used for troubleshooting.'
                  />
                  <FormCheckbox
                    control={control}
                    name='discoveryServicesLogs'
                    description='Service logs'
                    helperText='Logs from IP Fabric discovery services used for troubleshooting.'
                    disabled={limitDevices}
                  />
                  {renderErrorMessage(noOptionErrorMessage)}
                </div>
              </Card>

              {/* Card 2: Snapshot settings */}
              <Card
                className={`flex flex-col ${showDeviceSelection ? 'flex-1' : ''}`}
              >
                <div className='p flex flex-col gap overflow-hidden'>
                  <div className='flex items-center gap-1'>
                    <div className='text-level-5 font-semibold'>
                      Snapshot settings
                    </div>
                    <TooltipIcon content='Choose the snapshot scope and optional export filters.' />
                  </div>
                  <p className='text-sm text-default'>
                    Include the current snapshot or a full database dump, with
                    options to remove CLI logs or limit snapshot data to
                    selected devices.
                  </p>
                  <div className='flex flex-col gap'>
                    <RadioField
                      value={isNoSnapshotData}
                      onChange={(newVal) => {
                        if (newVal) {
                          setValue('snapshot', false);
                          setValue('databases', false);
                        }
                      }}
                      description="Don't include any snapshot data"
                    />
                    <RadioField
                      value={databases}
                      onChange={(newVal) => {
                        if (newVal) {
                          setValue('databases', true);
                          setValue('snapshot', false);
                        }
                      }}
                      description='Include data on all snapshots'
                    />
                    <RadioField
                      value={snapshot}
                      onChange={(newVal) => {
                        if (newVal) {
                          setValue('snapshot', true);
                          setValue('databases', false);
                        }
                      }}
                      description='Include data only for currently selected snapshot'
                    />

                    <div className='pl-8 flex flex-col gap'>
                      <FormCheckbox
                        control={control}
                        name='snapshotIncludeCli'
                        description='Include CLI logs from snapshots'
                        disabled={!snapshot}
                      />
                      <FormCheckbox
                        control={control}
                        name='snapshotBackupDb'
                        description='Include DB dump'
                        disabled={!snapshot || limitDevices}
                      />
                      <FormCheckbox
                        control={control}
                        name='limitDevices'
                        description='Include data only for selected devices'
                        disabled={!snapshot}
                      />
                    </div>
                  </div>
                </div>
                {renderErrorMessage(snapshotSettingsErrorMessage, 'px-4 pb-4')}
                {showDeviceSelection ? (
                  <>
                    <div className='border-t border-default' />
                    <div className='flex flex-col flex-1'>
                      <ApiTableView
                        model={deviceTableModel}
                        className='flex-1 mb'
                        borderless
                      />
                    </div>
                  </>
                ) : null}
              </Card>
            </fieldset>
          </form>
        )}
      </div>
      <GenerateTechsupportSideModal
        control={control}
        data={generateTechsupportModalData}
        formSubmitting={formState.isSubmitting}
        onClose={() => {
          setShowGenerateTechsupportModal(false);
        }}
        onGenerate={() => {
          void submitGenerateTechsupport().catch(() => undefined);
        }}
        onLoadSelectedSnapshotDeviceLabels={createSelectedSnapshotDeviceLabels}
        onVerifySnapshot={(values, limitSn) => {
          void requestVerifySnapshot(values, limitSn).catch(() => undefined);
        }}
        running={running}
        show={showGenerateTechsupportModal}
        snapshotId={snapshotId}
        verifyRunningJob={verifyRunningJob}
      />
    </div>
  );
};

export const Techsupport = memo(TechsupportPage);
